import { type HistoricalPrice, type CollectibleId } from "@/services/collectibles";
import type { CategoryId } from "@/services/collection/categories/categoriesSchema";
import { type ApiResponseImage } from "@/services/images";
import type { z } from "zod";
import { type Price, type PriceNumericOrEmpty } from "@/services/prices/priceTypes";
import type { CollectionItem_Schema, CollectionItemId_Schema } from "@/services/collection/item/collectionItemSchema";
import type { MarketplaceItemBasicInfo } from "@/services/marketplace/item";

export type CollectionItemId = z.infer<typeof CollectionItemId_Schema>;
export type CollectionItem = z.infer<typeof CollectionItem_Schema>;

export type CollectionItemWithMarketplaceItem = CollectionItem & {
  marketPlaceItem: MarketplaceItemBasicInfo;
};

export type CollectionItemWithCollectible = CollectionItem & {
  collectible: CollectionItemDetail_Collectible;
};

export type CollectionItemDetail = CollectionItemDetail_Collectible | CollectionItemDetail_LegoSet;

/** In long term we want to drop this specific detail. */
export type CollectionItemDetail_LegoSet = CollectionItemDetail_Collectible & {
  theme: { name: string };
  number: string;
  partsCount: string;
  year: string;
  dimensions: string;
  weight: string;
};

export type CollectionItemDetail_Collectible = {
  id: CollectibleId;
  name: string;
  prices: HistoricalPrice[];
  images: ApiResponseImage[];
  parameters: {
    option: {
      value: string;
    };
    parameter: {
      id: number;
      label: string;
      isWidgetIdentificator?: true;
    };
  }[];
};

export type CreateCollectionItemData = {
  categoryId: CategoryId;
  userCollectionListName?: string;
  aiIdentificationSourceId?: string;
} & (
  | {
      collectibleId: CollectibleId;
      // temp properties, will be removed after full lego migration into UU
      set: CollectibleId;
      minifigure: CollectibleId;
    }
  | {
      customName: string;
    }
);

export type ApiData_UpdateCollectionItem = Partial<{
  acquirePrice: PriceNumericOrEmpty;
  dateAcquired: string;
  currentPrice: Price;
  customName: string;
  note: string;
  publicNote: string;
  pieces: number;
  where_stored: string;
  acquisition_source: string;
  aiIdentificationSourceId: string;
}>;

export enum ItemStatus {
  InMyCollection = "inCollection",
  NotInCollection = "notInCollection",
  OnMarketplace = "onMarketplace",
  Deleted = "deleted",
  Expired = "expired",
  SoldOut = "soldOut",
}

export enum Api_ItemPatchOperation {
  Delete = "archivate",
}
